import React, { useState, useEffect } from 'react';
import { LanguageSelector } from './LanguageSelector';

export const Header = ({
  onLogoClick,
  onArticlesClick,
  onPatientStoriesClick,
  onSignUpClick,
  onPartnersClick,
  onCommunityClick,
  onAboutClick,
  bannerOffset = 0
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 80);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const linkStyle = {
    color: '#272726',
    textDecoration: 'none',
    fontSize: '15px',
    fontWeight: '500',
    padding: '10px 16px',
    borderRadius: '0',
    transition: 'all 0.25s ease',
    cursor: 'pointer',
    border: 'none',
    borderBottom: 'none',
    outline: 'none',
    background: 'transparent',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  };

  const mobileLinkStyle = {
    color: '#272726',
    textDecoration: 'none',
    fontSize: '18px',
    fontWeight: '500',
    padding: '16px 0',
    cursor: 'pointer',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    display: 'block',
    width: '100%',
    textAlign: 'left',
    borderBottom: '1px solid rgba(39, 39, 38, 0.1)'
  };

  const handleLinkHover = (e, isEnter) => {
    if (isEnter) {
      e.target.style.backgroundColor = 'rgba(39, 39, 38, 0.06)';
      e.target.style.borderRadius = '6px';
    } else {
      e.target.style.backgroundColor = 'transparent';
      e.target.style.borderRadius = '0';
    }
  };

  const signUpStyle = {
    ...linkStyle,
    backgroundColor: '#272726',
    color: '#fff',
    fontWeight: '600',
    borderRadius: '6px'
  };

  const handleMobileNavClick = (handler) => {
    setIsMobileMenuOpen(false);
    if (handler) handler();
  };

  return (
    <>
      {/* Mobile menu styles */}
      <style>{`
        @media (max-width: 768px) {
          .desktop-nav {
            display: none !important;
          }
          .mobile-menu-button {
            display: flex !important;
          }
        }
        @media (min-width: 769px) {
          .desktop-nav {
            display: flex !important;
          }
          .mobile-menu-button {
            display: none !important;
          }
        }
      `}</style>

      <header
        className="notranslate"
        style={{
          position: 'fixed',
          top: `${bannerOffset}px`,
          left: 0,
          right: 0,
          zIndex: 1000,
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: isScrolled || isMobileMenuOpen ? 'rgba(255, 255, 255, 0.98)' : 'transparent',
          boxShadow: isScrolled ? '0 2px 12px rgba(0, 0, 0, 0.08)' : 'none',
          transition: 'top 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease',
          backdropFilter: isScrolled ? 'blur(10px)' : 'none'
        }}
      >
        {/* Logo */}
        <button
          onClick={onLogoClick}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            lineHeight: 1.1,
            zIndex: 1001
          }}
        >
          <span
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: '22px',
              fontWeight: '700',
              color: '#272726'
            }}
          >
            Real
          </span>
          <span style={{ position: 'relative', display: 'inline-block' }}>
            <span
              style={{
                position: 'absolute',
                left: '-2%',
                right: '-8%',
                bottom: '0',
                height: '50%',
                background: 'linear-gradient(90deg, rgba(235, 160, 155, 0.85) 0%, rgba(245, 185, 165, 0.8) 40%, rgba(250, 200, 160, 0.75) 70%, rgba(245, 215, 170, 0.6) 100%)',
                transform: 'skewX(-2deg) rotate(-1deg)',
                zIndex: -1,
                borderRadius: '3px 6px 5px 3px'
              }}
            />
            <span
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: '22px',
                fontWeight: '700',
                color: '#272726'
              }}
            >
              Patients
            </span>
            <span
              style={{
                fontSize: '10px',
                fontWeight: '400',
                verticalAlign: 'super',
                marginLeft: '2px',
                color: '#272726'
              }}
            >
              ™
            </span>
          </span>
        </button>

        {/* Desktop Navigation */}
        <nav
          className="desktop-nav"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <button
            onClick={onAboutClick}
            style={linkStyle}
            onMouseEnter={(e) => handleLinkHover(e, true)}
            onMouseLeave={(e) => handleLinkHover(e, false)}
          >
            Our Story
          </button>
          {onPartnersClick && (
            <button
              onClick={onPartnersClick}
              style={linkStyle}
              onMouseEnter={(e) => handleLinkHover(e, true)}
              onMouseLeave={(e) => handleLinkHover(e, false)}
            >
              Companies/Partners
            </button>
          )}
          {onCommunityClick && (
            <button
              onClick={onCommunityClick}
              style={linkStyle}
              onMouseEnter={(e) => handleLinkHover(e, true)}
              onMouseLeave={(e) => handleLinkHover(e, false)}
            >
              Community
            </button>
          )}
          <LanguageSelector />
          <button
            onClick={onSignUpClick}
            style={signUpStyle}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#3a3a39'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#272726'}
          >
            Sign Up
          </button>
        </nav>

        {/* Mobile Menu Button (Hamburger) */}
        <button
          className="mobile-menu-button"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          style={{
            display: 'none',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            width: '44px',
            height: '44px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            zIndex: 1001
          }}
          aria-label="Toggle menu"
        >
          <span
            style={{
              display: 'block',
              width: '24px',
              height: '2px',
              backgroundColor: '#272726',
              transition: 'all 0.3s ease',
              transform: isMobileMenuOpen ? 'rotate(45deg) translateY(6px)' : 'none'
            }}
          />
          <span
            style={{
              display: 'block',
              width: '24px',
              height: '2px',
              backgroundColor: '#272726',
              margin: '6px 0',
              transition: 'all 0.3s ease',
              opacity: isMobileMenuOpen ? 0 : 1
            }}
          />
          <span
            style={{
              display: 'block',
              width: '24px',
              height: '2px',
              backgroundColor: '#272726',
              transition: 'all 0.3s ease',
              transform: isMobileMenuOpen ? 'rotate(-45deg) translateY(-6px)' : 'none'
            }}
          />
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="notranslate"
          style={{
            position: 'fixed',
            top: `${bannerOffset + 70}px`,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#fff',
            zIndex: 999,
            padding: '24px 20px',
            overflowY: 'auto'
          }}
        >
          <nav style={{ display: 'flex', flexDirection: 'column' }}>
            <LanguageSelector mobile />
            <button
              onClick={() => handleMobileNavClick(onAboutClick)}
              style={mobileLinkStyle}
            >
              Our Story
            </button>
            {onPartnersClick && (
              <button
                onClick={() => handleMobileNavClick(onPartnersClick)}
                style={mobileLinkStyle}
              >
                Companies/Partners
              </button>
            )}
            {onCommunityClick && (
              <button
                onClick={() => handleMobileNavClick(onCommunityClick)}
                style={mobileLinkStyle}
              >
                Community
              </button>
            )}
            <button
              onClick={() => handleMobileNavClick(onSignUpClick)}
              style={{
                ...mobileLinkStyle,
                backgroundColor: '#272726',
                color: '#fff',
                textAlign: 'center',
                borderRadius: '8px',
                marginTop: '24px',
                padding: '18px',
                fontWeight: '600',
                border: 'none'
              }}
            >
              Sign Up
            </button>
          </nav>
        </div>
      )}
    </>
  );
};
